/**
 * FRIDGE-1085 — brand pages order products biggest to smallest.
 *
 * Owner: "on /brands/ pages should show in order of biggest to smaller stuff … for example
 * all larger and then refrigerators alone, and then freezers alone and then FILTERS, AND
 * THEN PARTS". Amendment (FRIDGE-1090 rule 7): "this also includes the columns, blocks, etc".
 * This supersedes the "stable mixed default" sort recorded as an owner ruling of 07/04/2026.
 *
 * The open question was what counts as "large" when most rows carry no capacity. Owner's
 * ruling 09/19/2026: "full size / major / size / etc" — so size decides when it is known,
 * and the full-size/major CONFIGURATION decides when it is not. Both, in that order, never
 * a guess at a missing number.
 *
 * Specialty cooling (wine, beverage, kegerator, ice makers, drawers) sits in REFRIGERATORS,
 * after the large units. They are refrigeration, not freezers, and not parts; the owner's
 * five groups have no sixth bucket and inventing one would be a second convention.
 *
 * Pure, data-free and side-effect free so the ordering can be tested against the whole type
 * taxonomy without a database.
 */

/** The owner's five groups, in his order. */
export const BRAND_GROUPS = ['large-refrigerators', 'refrigerators', 'freezers', 'filters', 'parts'] as const
export type BrandGroup = (typeof BRAND_GROUPS)[number]

export const BRAND_GROUP_HEADINGS: Readonly<Record<BrandGroup, string>> = {
  'large-refrigerators': 'Large refrigerators',
  refrigerators: 'Refrigerators',
  freezers: 'Freezers',
  filters: 'Filters',
  parts: 'Parts',
}

/**
 * A full-size kitchen refrigerator by CONFIGURATION. Used only when capacity is unknown.
 * "top-freezer" and "bottom-freezer" name where the freezer compartment sits — they are
 * refrigerators, not freezers, and misreading them is the obvious way to get this wrong.
 */
export const FULL_SIZE_REFRIGERATOR_TYPES: readonly string[] = [
  'french-door', 'side-by-side', '4-door-flex', 'top-freezer', 'bottom-freezer',
  'counter-depth', 'built-in', 'single-door',
]

/** Standalone freezers. A freezer compartment inside a refrigerator is not one of these. */
export const FREEZER_TYPES: readonly string[] = [
  'upright-freezer', 'chest-freezer', 'deep-freezer', 'compact-freezer', 'drawer-freezer', 'freezer',
]

export const FILTER_TYPES: readonly string[] = [
  'water-filter', 'air-filter', 'ice-machine-filter', 'inline-filter',
]

export const PART_TYPES: readonly string[] = ['part', 'sensor', 'gasket']

/**
 * Cubic feet at or above which a refrigerator counts as large. A full-size US kitchen
 * refrigerator starts around 15; below it sits apartment, compact and specialty cooling.
 * Measured 09/19/2026 on active pdp_eligible rows: french-door averages 24.3, side-by-side
 * 22.6, bottom-freezer 17.7 and top-freezer 15.2, while compact averages 3.4 and wine
 * coolers 4.9. The line falls in the gap rather than through a cluster.
 */
export const LARGE_REFRIGERATOR_MIN_CUFT = 15

export interface BrandOrderable {
  type: string | null
  capacity_cuft?: number | null
}

/** Which of the owner's five groups a product belongs to. */
export function brandProductGroup(p: BrandOrderable): BrandGroup {
  const type = (p.type ?? '').toLowerCase()
  if (PART_TYPES.includes(type)) return 'parts'
  if (FILTER_TYPES.includes(type)) return 'filters'
  if (FREEZER_TYPES.includes(type)) return 'freezers'

  // Size decides when it is recorded — a 28 cu ft unit is large whatever its type name.
  const cuft = p.capacity_cuft
  if (typeof cuft === 'number' && Number.isFinite(cuft)) {
    return cuft >= LARGE_REFRIGERATOR_MIN_CUFT ? 'large-refrigerators' : 'refrigerators'
  }
  // No capacity on file: the full-size configuration decides. Nothing is inferred from a
  // missing number, and an unknown never promotes a compact into the large group.
  return FULL_SIZE_REFRIGERATOR_TYPES.includes(type) ? 'large-refrigerators' : 'refrigerators'
}

export function brandGroupRank(p: BrandOrderable): number {
  return BRAND_GROUPS.indexOf(brandProductGroup(p))
}

/**
 * Total order: group first, upright before chest freezers, then biggest first
 * within each configuration and a stable tiebreak.
 * A product with no capacity sorts after every product that has one, because "unknown" is
 * not "small" and must not jump ahead of a measured unit.
 */
export function compareBrandProducts<T extends BrandOrderable & { id?: number | string | null }>(a: T, b: T): number {
  const g = brandGroupRank(a) - brandGroupRank(b)
  if (g !== 0) return g
  if (brandProductGroup(a) === 'freezers') {
    const freezerRank = (type: string | null) => type?.toLowerCase() === 'upright-freezer' ? 0 : type?.toLowerCase() === 'chest-freezer' ? 1 : 2
    const configuration = freezerRank(a.type) - freezerRank(b.type)
    if (configuration !== 0) return configuration
  }

  const ac = typeof a.capacity_cuft === 'number' && Number.isFinite(a.capacity_cuft) ? a.capacity_cuft : null
  const bc = typeof b.capacity_cuft === 'number' && Number.isFinite(b.capacity_cuft) ? b.capacity_cuft : null
  if (ac !== null && bc !== null && ac !== bc) return bc - ac
  if (ac !== null && bc === null) return -1
  if (ac === null && bc !== null) return 1

  if (typeof a.id === 'number' && typeof b.id === 'number') return a.id - b.id
  const aid = String(a.id ?? '')
  const bid = String(b.id ?? '')
  return aid < bid ? -1 : aid > bid ? 1 : 0
}

/** The groups present in a set, in the owner's order. Empty groups are left out. */
export function brandGroupsPresent(products: readonly BrandOrderable[]): BrandGroup[] {
  const seen = new Set(products.map(brandProductGroup))
  return BRAND_GROUPS.filter((g) => seen.has(g))
}

/** PostgREST predicates mirror brandProductGroup, including SQL NULL semantics. */
export function brandGroupFilter(group: BrandGroup): string {
  if (group === 'filters') return `type.in.(${FILTER_TYPES.join(',')})`
  if (group === 'parts') return `type.in.(${PART_TYPES.join(',')})`
  if (group === 'freezers') return `type.in.(${FREEZER_TYPES.join(',')})`
  const cooling = `or(type.is.null,type.not.in.(${[...FREEZER_TYPES, ...FILTER_TYPES, ...PART_TYPES].join(',')}))`
  const fullSize = FULL_SIZE_REFRIGERATOR_TYPES.join(',')
  const size = group === 'large-refrigerators'
    ? `or(capacity_cuft.gte.${LARGE_REFRIGERATOR_MIN_CUFT},and(capacity_cuft.is.null,type.in.(${fullSize})))`
    : `or(capacity_cuft.lt.${LARGE_REFRIGERATOR_MIN_CUFT},and(capacity_cuft.is.null,or(type.is.null,type.not.in.(${fullSize}))))`
  return `and(${cooling},${size})`
}

/** Translate a global page into bounded ranges within ordered groups. */
export function brandPageRanges(counts: readonly number[], offset: number, limit: number) {
  const ranges: Array<{ group: BrandGroup; from: number; to: number }> = []
  let start = 0
  for (const [index, group] of BRAND_GROUPS.entries()) {
    const count = counts[index] ?? 0
    const from = Math.max(0, offset - start)
    const to = Math.min(count, offset + limit - start) - 1
    if (from <= to) ranges.push({ group, from, to })
    start += count
  }
  return ranges
}

/** FRIDGE-1085: upright, then chest, then other standalone freezers.
 * These are query segments, not additional visible groups. Filters and parts
 * use the separate family catalog; no always-empty family count queries. */
export const BRAND_CATALOG_SEGMENTS = [
  'large-refrigerators', 'refrigerators', 'upright-freezer', 'chest-freezer', 'other-freezers',
] as const
export type BrandCatalogSegment = (typeof BRAND_CATALOG_SEGMENTS)[number]

export function brandCatalogSegmentFilter(segment: BrandCatalogSegment): string {
  if (segment === 'upright-freezer' || segment === 'chest-freezer') return `type.eq.${segment}`
  if (segment === 'other-freezers') return `type.in.(${FREEZER_TYPES.filter(type => type !== 'upright-freezer' && type !== 'chest-freezer').join(',')})`
  return brandGroupFilter(segment)
}

export function brandCatalogPageRanges(counts: readonly number[], offset: number, limit: number) {
  const ranges: Array<{ segment: BrandCatalogSegment; from: number; to: number }> = []
  let start = 0
  for (const [index, segment] of BRAND_CATALOG_SEGMENTS.entries()) {
    const count = counts[index] ?? 0
    const from = Math.max(0, offset - start)
    const to = Math.min(count, offset + limit - start) - 1
    if (from <= to) ranges.push({ segment, from, to })
    start += count
  }
  return ranges
}
